import * as React from "react";

import { AccessReportsWizardOption } from "@app/access-reports/shared";
import { cloneDeep, isEqual } from "lodash";
import { DragAndDropReordering } from "@app/shared/drag-and-drop-reordering";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { JSX } from "react";

interface ManageOptionsProps {
  /**
   * An optional prop that displays any additional elements that should be displayed at a per-option level
   */
  buildAdditionalIndividualProperty?: (option: AccessReportsWizardOption) => JSX.Element;
  /**
   * An optional prop that displays any additional elements that should be handled in this component
   */
  additionalProperties?: JSX.Element;
  /**
   * An optional prop that hides the input that allows for new options to be created
   */
  disableAddingNewOptions?: boolean;
  /**
   * An optional prop that hides the button that allows for an option to be deleted
   */
  disableDeletion?: boolean;
  /**
   * An optional prop that hides the button that allows for an option's name to be edited
   */
  disableNameEdit?: boolean;
  /**
   * An optional prop that hides the button that allows for drag and drop reordering of the options
   */
  disableReordering?: boolean;
  /**
   * Prevents the user from making modifications to the template
   */
  disableUserActions: boolean;
  /**
   * An optional prop that displays an alert if an option cannot be deleted due to connected options
   */
  getOptionConnections?: (optionId: number) => JSX.Element;
  /**
   * Handles saving any modifications done to the list of options
   */
  onSaveOptionsClick: (options: AccessReportsWizardOption[]) => void;
  /**
   * The list of options to be managed
   */
  options: AccessReportsWizardOption[];
  /**
   * The option type in order to accurately describe errors
   */
  optionType: string;
  /**
   * An optional prop that disables the save button if at least one option is not present
   */
  requireMinimumOne?: boolean;
  /**
   * An optional prop that overrides the check to determine the disabling of the save button;
   * This is useful in case the optional "additional properties" prop alters the options in any way
   */
  savedOptions?: AccessReportsWizardOption[];
}

interface ManageOptionsState {
  connectedOptionsWarningElement: JSX.Element;
  connectedOptionsWarningOptionId: number;
  draggable: boolean;
  newOptionName: string;
  optionNameBeingEdited: number;
  optionNameBeingEditedValue: string;
  options: AccessReportsWizardOption[];
}

class ManageOptions extends React.Component<ManageOptionsProps, ManageOptionsState> {
  constructor(props: ManageOptionsProps) {
    super(props);

    this.state = {
      connectedOptionsWarningElement: null,
      connectedOptionsWarningOptionId: null,
      draggable: false,
      newOptionName: "",
      optionNameBeingEdited: null,
      optionNameBeingEditedValue: "",
      options: [],
    };
  }

  public componentDidMount() {
    if (this.props.options.length) {
      this.setOptions();
    }
  }

  public componentDidUpdate(prevProps: ManageOptionsProps) {
    if (!isEqual(prevProps.options, this.props.options)) {
      this.setOptions();
    }
  }

  public render() {
    return (
      <React.Fragment>
        {!this.props.disableAddingNewOptions &&
          this.getAddOptionSection()
        }

        {!!this.props.additionalProperties &&
          this.props.additionalProperties
        }

        {this.state.options.length > 0 &&
          this.getOptionsList()
        }

        <hr />
        <FlyoutFooter
          submitButton={
            <QESButton
              className="ml-3"
              disabled={(this.props.requireMinimumOne && this.state.options.length < 1) || (
                !!this.props.savedOptions ?
                  isEqual(this.props.savedOptions, this.state.options) :
                  isEqual(this.props.options, this.state.options)
              ) || this.state.optionNameBeingEdited !== null ||
                this.props.disableUserActions
              }
              onClick={() => this.props.onSaveOptionsClick(this.state.options)}
              qesTestId="access-reports-admin__manage-options--save"
              qesType="primary"
            >
              Save
            </QESButton>
          }
        />
      </React.Fragment>
    );
  }

  public onDragAndDropChange = (items: JSX.Element[]): void => {
    // re-order the options based on the returned elements
    const options: AccessReportsWizardOption[] = [];

    for (const item of items) {
      // extract the option id from the element's id
      const optionId = item.props.id.replace(/\D/g, "");
      const option = this.state.options.find(x => x.id === parseInt(optionId));

      options.push(option);
    }

    this.setState({
      options,
    });
  }

  private onDragClick = () => {
    this.setState({
      draggable: true,
    })
  }

  private onDragEnd = (): void => {
    this.setState({
      draggable: false,
    })
  }

  private getAddOptionSection = (): JSX.Element => {
    const addOptionValidationText: string = this.getAddOptionValidationText();

    return (
      <div className="row">
        <div className="col-12">
          <QESTextInput
            inputAppend={(
              <QESButton
                qesType="secondary"
                className="p-1"
                disabled={!this.getAddOptionButtonValidity()}
                qesTestId="access-reports-admin__manage-options--add-option"
                onClick={() => this.onAddOptionClick()}
              >
                <i className="fa fa-plus fa-fw" />
              </QESButton>
            )}
            testId="access-reports-admin__manage-options--add-option-input"
            errorText={addOptionValidationText}
            disabled={this.props.disableUserActions}
            isValid={!addOptionValidationText}
            label={this.props.optionType}
            onChange={(e) => this.onOptionNameInputChange(e)}
            unrequiredValidation={true}
            value={this.state.newOptionName}
          />

          <hr />
        </div>
      </div>
    );
  }

  private getAddOptionButtonValidity = (): boolean => {
    const options: string[] = this.state.options.map(x => x.displayName ? x.displayName.toUpperCase() : x.name.toUpperCase());

    if (!this.props.disableUserActions &&
      !!this.state.newOptionName &&
      !options.includes(this.state.newOptionName.toUpperCase())
    ) {
      return true;
    }
  }

  private getAddOptionValidationText = (): string => {
    const options: string[] = this.state.options.map(x => x.displayName ? x.displayName.toUpperCase() : x.name.toUpperCase());

    if (options.includes(this.state.newOptionName.toUpperCase())) {
      return `A ${this.props.optionType} with that name already exists`;
    }
  }

  private getOptionNameBeingEditedValidationText = (): string => {
    if (!this.state.optionNameBeingEditedValue) {
      return `Please enter a ${this.props.optionType} name`;
    } else {
      const optionWithThisName: AccessReportsWizardOption = this.state.options.find(x => x.name === this.state.optionNameBeingEditedValue);

      if (!!optionWithThisName && this.state.optionNameBeingEdited !== optionWithThisName.id) {
        return `A ${this.props.optionType} with that name already exists`;
      } else {
        return "";
      }
    }
  }

  private getOptionsList = (): JSX.Element => {
    const elements: JSX.Element[] = this.state.options.map((option, optionIndex) => {
      const optionNameBeingEditedValidationText: string = (this.state.optionNameBeingEdited === option.id) && this.getOptionNameBeingEditedValidationText();

      // each option must have a unique id for the drag and drop reordering to work correctly
      return (
        <div id={`access-reports-admin__manage-options--option-container-${option.id}`} key={optionIndex}>
          <QESTextInput
            inputAppend={(
              <React.Fragment>
                {this.state.optionNameBeingEdited === option.id ? (
                  <QESButton
                    className="p-1"
                    disabled={!!optionNameBeingEditedValidationText}
                    qesTestId={`access-reports-admin__manage-options--save-name-${option.id}`}
                    qesType="secondary"
                    onClick={() => this.onSaveOptionNameClick(option.id)}
                    title="Save"
                  >
                    <i className="fa fa-check fa-fw" />
                  </QESButton>
                ) : (
                  <React.Fragment>
                    {!this.props.disableNameEdit && (
                      <QESButton
                        qesType="secondary"
                        className="p-1"
                        disabled={this.props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--edit-name-${option.id}`}
                        onClick={() => !this.props.disableUserActions && this.onEditOptionNameClick(option)}
                        title="Edit Name"
                      >
                        <i className="fa fa-pencil-square-o fa-fw" />
                      </QESButton>
                    )}

                    {!this.props.disableReordering && (
                      <QESButton
                        qesType="secondary"
                        className="p-1"
                        disabled={this.props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--drag-and-drop-${option.id}`}
                        onMouseDown={() => !this.props.disableUserActions && this.onDragClick()}
                        title="Reorder"
                      >
                        <i className="fa fa-arrows fa-fw" />
                      </QESButton>
                    )}

                    {!this.props.disableDeletion && (
                      <QESButton
                        className="p-1"
                        disabled={this.props.disableUserActions}
                        qesTestId={`access-reports-admin__manage-options--delete-${option.id}`}
                        onClick={() => !this.props.disableUserActions && this.onRemoveOptionClick(option.id)}
                        qesType="secondary-danger"
                        title="Delete"
                      >
                        <i className="fa fa-trash fa-fw" />
                      </QESButton>
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            testId={`access-reports-admin__manage-options--name-input-${option.id}`}
            errorText={optionNameBeingEditedValidationText}
            disabled={this.props.disableUserActions}
            hideLabel={true}
            isValid={!optionNameBeingEditedValidationText}
            label={this.state.optionNameBeingEdited === option.id ?
              this.state.optionNameBeingEditedValue :
              option.displayName ? option.displayName : option.name
            }
            readOnly={this.state.optionNameBeingEdited !== option.id}
            onChange={(event) => this.onOptionNameBeingEditedChange(event)}
            onKeyPress={(event) => this.onOptionNameBeingEditedKeyPress(event, optionNameBeingEditedValidationText, option.id)}
            unrequiredValidation={true}
            value={this.state.optionNameBeingEdited === option.id ?
              this.state.optionNameBeingEditedValue :
              option.displayName ? option.displayName : option.name
            }
          />

          {(!!this.state.connectedOptionsWarningElement && this.state.connectedOptionsWarningOptionId === option.id) ?
            this.state.connectedOptionsWarningElement :
            null
          }

          {!!this.props.buildAdditionalIndividualProperty &&
            this.props.buildAdditionalIndividualProperty(option)
          }
        </div>
      )
    });

    return (
      <div className="row">
        <div className="col-12">
          <DragAndDropReordering
            draggable={this.state.draggable}
            items={elements}
            onDragAndDropChange={this.onDragAndDropChange}
            onDragEnd={this.onDragEnd}
          />
        </div>
      </div>
    );
  }

  private onAddOptionClick = (): void => {
    const options: AccessReportsWizardOption[] = cloneDeep(this.state.options);
    let maxId = 0;
    if (!!options && options.length) {
      maxId = Math.max(...options.map((o) => o.id)) + 1;
    }

    const newOption: AccessReportsWizardOption = {
      defaultProducts: [],
      displayName: this.state.newOptionName,
      hiddenProducts: [],
      id: maxId,
      name: this.state.newOptionName,
    };

    options.push(newOption);

    this.setState({
      connectedOptionsWarningOptionId: null,
      newOptionName: "",
      options,
    });
  }

  private onEditOptionNameClick = (
    option: AccessReportsWizardOption
  ): void => {
    event.preventDefault();

    this.setState({
      optionNameBeingEdited: option.id,
      optionNameBeingEditedValue: option.name,
    });
  }

  private onOptionNameBeingEditedChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      optionNameBeingEditedValue: event.target.value,
    });
  }

  private onOptionNameBeingEditedKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement>,
    optionNameBeingEditedTooltipText: string,
    optionId: number
  ): void => {
    // allow for the user to save the option currently being edited
    // on enter key press if valid
    if (event.key === "Enter" && !optionNameBeingEditedTooltipText) {
      event.preventDefault();
      this.onSaveOptionNameClick(optionId)
    }
  }

  private onOptionNameInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    this.setState({
      newOptionName: event.target.value,
    });
  }

  private onRemoveOptionClick = (
    optionId: number,
  ): void => {
    if (!!this.props.getOptionConnections) {
      // determine if an option has any connected options prior to deletion;
      // if connected options do exist, display a warning
      const connectedOptionsWarningElement: JSX.Element = this.props.getOptionConnections(optionId);

      if (!!connectedOptionsWarningElement) {
        this.setState({
          connectedOptionsWarningElement,
          connectedOptionsWarningOptionId: optionId,
        });
      } else {
        const optionsClone: AccessReportsWizardOption[] = cloneDeep(this.state.options);
        const options: AccessReportsWizardOption[] = optionsClone.filter(x => x.id !== optionId);

        this.setState({
          options,
        });
      }
    } else {
      const optionsClone: AccessReportsWizardOption[] = cloneDeep(this.state.options);
      const options: AccessReportsWizardOption[] = optionsClone.filter(x => x.id !== optionId);

      this.setState({
        options,
      });
    }
  }

  private onSaveOptionNameClick = (
    optionId: number,
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    const options: AccessReportsWizardOption[] = cloneDeep(this.state.options);
    const option: AccessReportsWizardOption = options.find(x => x.id === optionId);
    if (!!option) {
      option.displayName = this.state.optionNameBeingEditedValue;
      option.name = this.state.optionNameBeingEditedValue;

      this.setState({
        optionNameBeingEdited: null,
        optionNameBeingEditedValue: "",
        options,
      });
    }
  }

  private setOptions = (): void => {
    // the direct options prop array is not modified
    // in case the user wishes to "cancel" any changes made
    this.setState({
      options: cloneDeep(this.props.options),
    });
  }
}

export { ManageOptions };
